import { ReactNode } from "react";

import { brand } from "@/config/tailwind-colors/brand-colors";
import { BRAND_AVANTI } from "@/constants";

// Reusable Components
interface TextProps {
  children: ReactNode;
  className?: string;
}

export const LabelText = ({ children, className = "" }: TextProps) => {
  const avantiLabelColor =
    brand === BRAND_AVANTI ? "[#2E2D2C]" : "text-functional-subtle";

  return (
    <span
      className={`font-body-font font-[400] text-[16px] leading-[24px] ${
        brand === BRAND_AVANTI
          ? `text-${avantiLabelColor}`
          : "text-text-functional-subtle"
      } align-middle ${className}`}
    >
      {children}
    </span>
  );
};

export const ValueText = ({ children, className = "" }: TextProps) => (
  <span
    className={`font-body-font font-[600] text-[16px] leading-[24px] text-text-title align-middle ${className}`}
  >
    {children}
  </span>
);

interface SectionTitleProps {
  children: ReactNode;
  testId?: string;
}

export const SectionTitle = ({ children, testId }: SectionTitleProps) => (
  <h3
    className="font-header-font font-[400] text-[20px] leading-[105%] tracking-[0%] text-text-title"
    data-testid={testId}
  >
    {children}
  </h3>
);

export const SubsectionTitle = ({
  children,
}: Omit<SectionTitleProps, "testId">) => {
  const subsectionColor =
    brand === BRAND_AVANTI ? "text-[#005A5E]" : "text-text-title";

  return (
    <h4
      className={`font-header-font font-[400] text-[20px] leading-[105%] tracking-[0%] ${subsectionColor}`}
    >
      {children}
    </h4>
  );
};

// Divider Component
interface DividerProps {
  className?: string;
  testId?: string;
}

export const Divider = ({
  className = "mb-spacing-xs",
  testId,
}: DividerProps) => (
  <div
    className={`border-b border-border-default ${className}`}
    data-testid={testId}
  />
);

// Collapsible Arrow Icon Component
interface CollapsibleArrowProps {
  isExpanded: boolean;
  testId?: string;
}

export const CollapsibleArrow = ({
  isExpanded,
  testId,
}: CollapsibleArrowProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
    className={`transition-transform duration-200 ease-in-out ${
      isExpanded ? "rotate-0" : "rotate-180"
    }`}
    data-testid={testId}
  >
    <path
      d="M12.75 6.75L6.75 0.75L0.75 6.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="stroke-text-title"
    />
  </svg>
);

// Info Row Component (Label + Value pair)
interface InfoRowProps {
  label: string;
  value?: ReactNode;
  values?: string[];
  className?: string;
  testId?: string;
  valueClassName?: string;
  labelClassName?: string;
}

export const InfoRow = ({
  label,
  value,
  values,
  className = "mb-spacing-xs flex justify-between items-center",
  testId,
  valueClassName,
  labelClassName,
}: InfoRowProps) => {
  const hasMultipleValues = values && values.length > 0;
  const containerClass = hasMultipleValues
    ? className.replace("items-center", "items-start")
    : className;

  return (
    <div className={containerClass} data-testid={testId}>
      <LabelText className={labelClassName}>{label}</LabelText>
      {hasMultipleValues ? (
        <div className="flex flex-col items-end">
          {values.map((val) => (
            <ValueText key={`${label}-${val}`} className="text-right">
              {val}
            </ValueText>
          ))}
        </div>
      ) : (
        <ValueText className={valueClassName}>{value}</ValueText>
      )}
    </div>
  );
};

// Edit Button Component
interface EditButtonProps {
  onClick: () => void;
  label: string;
  testId?: string;
  className?: string;
}

export const EditButton = ({
  onClick,
  label,
  testId,
  className = "mt-spacing-m flex justify-end",
}: EditButtonProps) => {
  const buttonTextColor =
    brand === BRAND_AVANTI
      ? "text-[#306582]"
      : "text-interactive-secondary-default";
  const buttonDecorationColor =
    brand === BRAND_AVANTI
      ? "decoration-[#306582]"
      : "decoration-interactive-secondary-default";

  return (
    <div className={className}>
      <button
        type="button"
        className={`font-body-font font-[600] text-[16px] leading-[24px] ${buttonTextColor} bg-transparent border-none cursor-pointer p-0 underline underline-offset-4 ${buttonDecorationColor}`}
        onClick={onClick}
        data-testid={testId}
      >
        {label}
      </button>
    </div>
  );
};

// Section Header Component (Title + Collapsible Arrow)
interface SectionHeaderProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  titleTestId?: string;
  arrowTestId?: string;
}

export const SectionHeader = ({
  title,
  isExpanded,
  onToggle,
  titleTestId,
  arrowTestId,
}: SectionHeaderProps) => (
  <button
    type="button"
    className="flex justify-between items-center cursor-pointer w-full border-0 bg-transparent p-0 text-left"
    onClick={onToggle}
  >
    <SectionTitle testId={titleTestId}>{title}</SectionTitle>
    <CollapsibleArrow isExpanded={isExpanded} testId={arrowTestId} />
  </button>
);
